import { type ReactNode } from 'react'
import { cn } from '@/lib/utils'

type Props = {
  children: ReactNode
  className?: string
  href?: string
  onClick?: () => void
  /** kept for call-site compatibility; no longer used */
  strength?: number
  as?: 'a' | 'button'
  ariaLabel?: string
  // forwarded data- attributes (e.g. data-cursor)
  [key: `data-${string}`]: string | undefined
}

/**
 * Primary button/link. Formerly a cursor-following "magnetic" control; now a
 * calm, smooth scale-up on hover to match the rest of the site's CTAs. The name
 * and props are unchanged so the 50-odd call sites need no edits.
 */
export function MagneticButton({
  children,
  className,
  href,
  onClick,
  as,
  ariaLabel,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  strength,
  ...rest
}: Props) {
  const Tag = (as ?? (href ? 'a' : 'button')) as 'a'
  return (
    <Tag
      href={href}
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center justify-center transition-transform duration-300 ease-brand hover:scale-[1.02] motion-reduce:hover:scale-100',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  )
}
